import { Transition, TransitionChild } from "@headlessui/react";
import Image from "next/image";
import Link from "next/link";
import { MdArrowOutward, MdChevronLeft, MdChevronRight, MdClose, MdOutlineCheckroom, MdSearch } from "react-icons/md";

import remixImage from "public/images/remix.png";
import { ROLES_WITH_STORE_ACCESS } from "@/constants/user";
import { useLogout } from "@/hooks/use-logout";
import { useAuthStore } from "@/stores/use-auth-store";
import { useUserStore } from "@/stores/use-user-store";

import { HEADER_SECTIONS, type ActiveFlyout, type SectionKey } from "../Sections";
import { MobileSectionContent } from "./MobileSectionContent";

import type { JSX } from "react";
import { Button } from "shared-components";
import { RxExit } from "react-icons/rx";
import { TbBox } from "react-icons/tb";
import { FaStore } from "react-icons/fa6";

interface Props {
  isOpen: boolean;
  /** Seção aberta dentro do menu (null = lista de categorias). Busca é tratada pelo `MobileSearch`. */
  activeFlyout: ActiveFlyout;
  expandedTopicId: string | null;
  onClose: () => void;
  onSelectSection: (sectionKey: SectionKey) => void;
  onBack: () => void;
  onToggleTopic: (topicId: string) => void;
  onOpenSearch: () => void;
}

const roundCloseButtonClassName = "flex h-5 w-5 items-center justify-center rounded-full bg-base-input-opacity-50";
const outlineButtonClassName =
  "flex h-5 flex-1 items-center justify-center rounded-full border border-base-border font-bold";

/** Bloco de conta no rodapé do menu: login/cadastro ou atalhos do usuário logado. */
const SidebarAccount = ({ onClose }: { onClose: () => void }): JSX.Element => {
  const logout = useLogout();
  const { activeUser } = useUserStore();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAssociate = activeUser ? ROLES_WITH_STORE_ACCESS.includes(activeUser.role) : false;

  if (!isAuthenticated || !activeUser) {
    return (
      <div className="flex gap-1">
        <Link href="/login" onClick={onClose} className={outlineButtonClassName}>
          Entrar
        </Link>
        <Link href="/cadastro" onClick={onClose} className={outlineButtonClassName}>
          Cadastre-se
        </Link>
      </div>
    );
  }

  return (
    <div className="p-1 bg-border-light rounded-3xl flex flex-col">
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <div className="bg-brand-primary rounded-full h-5 w-5 flex items-center justify-center">
            <p className="text-base-white font-bold">{activeUser.name[0].toUpperCase()}</p>
          </div>
          <div>
            <h3 className="text-base font-semibold text-brand-primary">Olá, {activeUser.name.split(" ")[0]}</h3>
            <Link href="/meu-perfil">
              <p className="text-xs underline">Ver meu perfil</p>
            </Link>
          </div>
        </div>
        <div>
          <Button
            className="bg-transparent"
            onClick={() => {
              logout();
              onClose();
            }}
          >
            <RxExit className="text-base-black text-xl" />
          </Button>
        </div>
      </div>
      <div className="flex gap-2 mt-1">
        <div className="bg-base-white p-2 rounded-2xl flex-1">
          <Link href="/meus-pedidos" className="flex items-center justify-center flex-col gap-1">
            <TbBox className="text-2xl" />
            <p className="text-xs font-semibold">Meus Pedidos</p>
          </Link>
        </div>
        {isAssociate && process.env.NEXT_PUBLIC_STORE_MANAGEMENT_URL && (
          <div className="bg-base-white p-2 rounded-2xl flex-1">
            <button
              type="button"
              onClick={() => window.location.assign(process.env.NEXT_PUBLIC_STORE_MANAGEMENT_URL as string)}
              className="flex items-center justify-center w-full flex-col gap-1"
            >
              <FaStore className="text-2xl" />
              <p className="text-xs text-center font-semibold">Minha loja</p>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Menu lateral mobile (Headless UI Transition). Tem duas visões controladas por `activeFlyout`:
 * lista de categorias (null) e conteúdo da seção (accordion controlado por `expandedTopicId`).
 * O estado de aberto/fechado (`isOpen`) é independente dos demais.
 */
export const MobileSidebar = ({
  isOpen,
  activeFlyout,
  expandedTopicId,
  onClose,
  onSelectSection,
  onBack,
  onToggleTopic,
  onOpenSearch,
}: Props): JSX.Element => {
  const activeSection = HEADER_SECTIONS.find((section) => section.key === activeFlyout);

  return (
    <Transition show={isOpen} as="div" className="fixed inset-0 z-40 lg:hidden">
      <TransitionChild>
        <div
          className="absolute inset-0 bg-[rgba(0,0,0,0.4)] transition duration-200 ease-out data-[closed]:opacity-0"
          onClick={onClose}
        />
      </TransitionChild>

      <TransitionChild>
        <aside
          className={
            "absolute inset-y-0 left-0 flex w-[19rem] max-w-[88vw] flex-col bg-base-white p-2 " +
            "transition duration-200 ease-out data-[closed]:-translate-x-full"
          }
        >
          {activeSection ? (
            <>
              <div className="mb-3 flex items-center justify-between">
                <button type="button" onClick={onBack} className="flex items-center text-sm font-bold">
                  <MdChevronLeft size="1.25rem" />
                  Voltar
                </button>

                <button type="button" aria-label="Fechar menu" onClick={onClose} className={roundCloseButtonClassName}>
                  <MdClose size="1.25rem" />
                </button>
              </div>

              <div className="grow overflow-y-auto">
                <MobileSectionContent
                  sectionKey={activeSection.key}
                  expandedTopicId={expandedTopicId}
                  onToggleTopic={onToggleTopic}
                  onNavigate={onClose}
                />
              </div>

              <Link
                href="/customizar-produto"
                onClick={onClose}
                className="mt-2 flex items-center gap-2 rounded-2xl border border-base-border bg-base-gray-opacity-5 p-2"
              >
                <MdOutlineCheckroom size="2rem" className="min-w-4" />
                <span className="text-sm">
                  <b className="block">Crie a sua camiseta</b>
                  Escolha o blank e envie sua arte.
                </span>
              </Link>
            </>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between">
                <Image src={remixImage} alt="Remix" className="h-auto w-[6.5rem]" sizes="104px" />

                <button type="button" aria-label="Fechar menu" onClick={onClose} className={roundCloseButtonClassName}>
                  <MdClose size="1.25rem" />
                </button>
              </div>

              <button
                type="button"
                onClick={onOpenSearch}
                className="mb-3 flex h-5 w-full items-center gap-1 rounded-full bg-base-input-opacity-50 px-2 text-start text-sm"
              >
                <MdSearch size="1.25rem" />
                Busque estampas ou produtos
              </button>

              <b className="mb-1 text-xs uppercase tracking-wider text-brand-primary">Categorias</b>

              <nav className="grow overflow-y-auto">
                {HEADER_SECTIONS.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    onClick={() => onSelectSection(section.key)}
                    className="flex w-full items-center justify-between py-[0.6rem] text-start"
                  >
                    {section.name}
                    <MdChevronRight size="1.25rem" />
                  </button>
                ))}

                <Link
                  href="/customizar-produto"
                  onClick={onClose}
                  className="block py-[0.6rem] font-semibold text-brand-accent"
                >
                  Customizar produto
                </Link>
              </nav>
              <SidebarAccount onClose={onClose} />
              <div className="flex flex-col gap-1 pt-2">
                <Link
                  href="/associacao"
                  onClick={onClose}
                  className="flex h-5 items-center justify-center gap-0.5 rounded-full bg-brand-primary font-bold text-base-white"
                >
                  Vender no Remix
                  <MdArrowOutward size="0.875rem" className="text-brand-accent" />
                </Link>
              </div>
            </>
          )}
        </aside>
      </TransitionChild>
    </Transition>
  );
};
